import { useEffect, useState } from "react";
import { Receipt, CheckSquare, Square, Stack } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { JOB_STATUS_LABELS, inr } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const groupKey = (j) => `${j.bank_name || "Unknown"}${j.branch_name ? ` — ${j.branch_name}` : ""}`;

export default function BulkInvoices() {
  const [jobs, setJobs] = useState(null);
  const [group, setGroup] = useState("");
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ bill_to_name: "", bill_to_address: "", bill_to_email: "", bill_to_gstin: "", tax_rate: "18", due_date: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  if (!jobs) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading jobs…</div>;

  const eligible = jobs.filter((j) => ["client_approved", "report_released", "completed"].includes(j.status) && !j.invoiced);
  const groups = {};
  eligible.forEach((j) => { (groups[groupKey(j)] = groups[groupKey(j)] || []).push(j); });
  const groupNames = Object.keys(groups).sort();
  const rows = group ? groups[group] || [] : [];
  const chosen = rows.filter((j) => selected.includes(j.id));
  const subtotal = chosen.reduce((s, j) => s + (j.fee || 0), 0);
  const tax = subtotal * (parseFloat(form.tax_rate) || 0) / 100;

  const pickGroup = (g) => {
    setGroup(g);
    setSelected((groups[g] || []).map((j) => j.id));
    setForm((f) => ({ ...f, bill_to_name: g.split(" — ")[0] }));
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const create = async () => {
    if (!form.bill_to_name) { toast.error("Enter who to bill"); return; }
    if (chosen.length === 0) { toast.error("Select at least one job"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/bulk-from-jobs", {
        ...form, tax_rate: parseFloat(form.tax_rate) || 0,
        due_date: form.due_date || null, job_ids: chosen.map((j) => j.id),
      });
      toast.success(`Consolidated invoice ${data.invoice_number} created`);
      setSelected([]); setGroup("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="bulk-invoices-page">
      <PageHeader overline="Invoices" title="Bulk / Recurring Invoices" subtitle="Banks usually settle valuation fees monthly — roll several completed jobs into one invoice" />

      {groupNames.length === 0 ? (
        <EmptyState icon={Receipt}>No delivered or closed jobs waiting to be billed. Complete a few jobs and they'll appear here.</EmptyState>
      ) : (
        <>
          <Card className="p-6 border-border">
            <Label className="mb-2 block">1. Pick a bank / branch to consolidate</Label>
            <div className="flex flex-wrap gap-2">
              {groupNames.map((g) => (
                <button key={g} onClick={() => pickGroup(g)} data-testid={`bulk-group-${g.replace(/\W+/g, "-")}`}
                  className={`px-3 py-2 rounded-lg text-sm border text-left transition-colors duration-150 ${group === g ? "bg-primary text-white border-primary" : "bg-white border-border text-primary hover:border-accent"}`}>
                  <div className="font-medium">{g}</div>
                  <div className={`text-[11px] ${group === g ? "text-white/60" : "text-muted-foreground"}`}>{groups[g].length} jobs · {inr(groups[g].reduce((s, j) => s + (j.fee || 0), 0))}</div>
                </button>
              ))}
            </div>
          </Card>

          {group && (
            <>
              <Card className="p-6 border-border">
                <Label className="mb-3 block">2. Choose the jobs to include</Label>
                <div className="space-y-1.5">
                  {rows.map((j) => {
                    const on = selected.includes(j.id);
                    return (
                      <button key={j.id} onClick={() => toggle(j.id)} data-testid={`bulk-job-${j.id}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-md border text-left transition-colors duration-150 ${on ? "bg-accent/10 border-accent/40" : "bg-muted border-border hover:border-accent/40"}`}>
                        {on ? <CheckSquare size={19} weight="fill" className="text-accent shrink-0" /> : <Square size={19} className="text-muted-foreground shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-primary truncate">{j.applicant_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{j.reference} · {JOB_STATUS_LABELS[j.status]}</div>
                        </div>
                        <div className="text-sm tabular text-primary shrink-0">{inr(j.fee)}</div>
                      </button>
                    );
                  })}
                </div>
              </Card>

              <Card className="p-6 border-border space-y-3">
                <Label className="block">3. Billing details</Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Bill To *</Label><Input value={form.bill_to_name} onChange={set("bill_to_name")} data-testid="bulk-bill-to" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={form.bill_to_email} onChange={set("bill_to_email")} data-testid="bulk-email" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">GSTIN</Label><Input value={form.bill_to_gstin} onChange={set("bill_to_gstin")} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">GST Rate (%)</Label>
                    <Select value={form.tax_rate} onValueChange={set("tax_rate")}>
                      <SelectTrigger data-testid="bulk-tax"><SelectValue /></SelectTrigger>
                      <SelectContent>{["0", "5", "12", "18", "28"].map((r) => <SelectItem key={r} value={r}>{r}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Due Date</Label><DatePicker value={form.due_date} onChange={set("due_date")} placeholder="Select date" clearable className="w-full" data-testid="bulk-due" /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Billing Address</Label><Textarea rows={2} value={form.bill_to_address} onChange={set("bill_to_address")} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={set("notes")} placeholder="e.g. Monthly consolidated bill for August 2026" /></div>

                <div className="rounded-md bg-primary text-white p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="text-white/60 text-xs uppercase tracking-wider">{chosen.length} jobs · subtotal {inr(subtotal)} · GST {inr(tax)}</div>
                    <div className="font-display font-black text-2xl mt-0.5 tabular" data-testid="bulk-total">{inr(subtotal + tax)}</div>
                  </div>
                  <Button onClick={create} disabled={busy || chosen.length === 0} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="create-bulk-btn">
                    <Stack size={16} className="mr-1" /> {busy ? "Creating…" : "Create Consolidated Invoice"}
                  </Button>
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
